/**
 * CaribbeanLeaf Authentication Context
 *
 * Provides multi-tenant authentication state for cannabis dispensary POS.
 * Stores JWT tokens in localStorage and auto-initializes on mount.
 *
 * Features:
 * - Multi-tenant login with tenant_slug
 * - Token persistence and auto-restore
 * - Token refresh handling
 * - Logout event listening for 401 responses
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { apiClient, setTokens, clearTokens, getToken, setTenantSlug } from '@/lib/api-client';
import { ENDPOINTS } from '@/config/api';
import type {
  CannabisUser,
  TenantInfo,
  CannabisLoginRequest,
  CannabisLoginResponse,
} from '@/types/api';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface AuthState {
  user: CannabisUser | null;
  tenant: TenantInfo | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

interface AuthContextValue extends AuthState {
  login: (credentials: CannabisLoginRequest) => Promise<void>;
  logout: () => void;
  refreshAuth: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const AuthContext = createContext<AuthContextValue | null>(null);

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    tenant: null,
    isAuthenticated: false,
    isLoading: true,
  });

  /**
   * Initialize auth state from stored token on mount
   */
  useEffect(() => {
    const initAuth = async () => {
      const token = getToken();

      if (token) {
        try {
          // Verify token and get current user
          const meResponse = await apiClient.get<{
            user_id: number;
            email: string;
            first_name: string | null;
            last_name: string | null;
            role: string;
            tenant: {
              slug: string;
              business_name: string;
              jurisdiction: string;
            };
          }>(ENDPOINTS.auth.me);

          // Persist tenant slug for API headers
          setTenantSlug(meResponse.tenant.slug);

          setState({
            user: {
              id: meResponse.user_id,
              email: meResponse.email,
              first_name: meResponse.first_name,
              last_name: meResponse.last_name,
              full_name:
                [meResponse.first_name, meResponse.last_name]
                  .filter(Boolean)
                  .join(' ') || meResponse.email,
              role: meResponse.role as CannabisUser['role'],
              is_active: true,
            },
            tenant: {
              id: 0,
              slug: meResponse.tenant.slug,
              business_name: meResponse.tenant.business_name,
              jurisdiction: meResponse.tenant.jurisdiction,
            },
            isAuthenticated: true,
            isLoading: false,
          });
          return;
        } catch {
          // Token invalid - clear it
          clearTokens();
        }
      }

      setState(prev => ({ ...prev, isLoading: false }));
    };

    initAuth();
  }, []);

  /**
   * Listen for logout events from API client (401 responses)
   */
  useEffect(() => {
    const handleLogout = () => {
      setState({
        user: null,
        tenant: null,
        isAuthenticated: false,
        isLoading: false,
      });
    };

    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  /**
   * Login with email, password, and tenant slug
   */
  const login = useCallback(async (credentials: CannabisLoginRequest) => {
    const response = await apiClient.post<CannabisLoginResponse>(
      ENDPOINTS.auth.login,
      credentials
    );

    // Store tokens and tenant slug for API headers
    setTokens(response.access_token, response.refresh_token);
    setTenantSlug(response.tenant?.slug || credentials.tenant_slug);

    // Update state
    setState({
      user: response.user,
      tenant: response.tenant,
      isAuthenticated: true,
      isLoading: false,
    });
  }, []);

  /**
   * Logout and clear all auth state
   */
  const logout = useCallback(async () => {
    // Server-side revocation before clearing local tokens
    const { serverLogout } = await import('@/lib/api-client');
    await serverLogout();
    setState({
      user: null,
      tenant: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  /**
   * Refresh the authentication token
   */
  const refreshAuth = useCallback(async () => {
    const refreshToken = localStorage.getItem('caribbeanleaf_refresh_token');

    if (!refreshToken) {
      logout();
      return;
    }

    try {
      const response = await apiClient.post<{
        access_token: string;
        refresh_token: string;
      }>(ENDPOINTS.auth.refresh, { refresh_token: refreshToken });

      setTokens(response.access_token, response.refresh_token);
    } catch {
      logout();
    }
  }, [logout]);

  return (
    <AuthContext.Provider
      value={{
        ...state,
        login,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
